import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, LogOut, HelpCircle, Settings, Shield, MessageCircle, BookOpen } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import ProjectSelector from './ProjectSelector';
import { useOnboarding } from '../contexts/OnboardingContext';
import NotificationCenter from './NotificationCenter';

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const { showOnboardingForReturningUser } = useOnboarding();
  const navigate = useNavigate();
  const location = useLocation();

  const PROJECT_INDEPENDENT_ROUTES = ['/app/intelligence', '/app/project-management', '/app/bd-engagements', '/app/tasks'];
  const showProjectSelector = !PROJECT_INDEPENDENT_ROUTES.some(r => location.pathname.startsWith(r));
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showHelpMenu, setShowHelpMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const userButtonRef = useRef<HTMLButtonElement>(null);
  const helpMenuRef = useRef<HTMLDivElement>(null);
  const helpButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        showUserMenu &&
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node) &&
        userButtonRef.current &&
        !userButtonRef.current.contains(event.target as Node)
      ) {
        setShowUserMenu(false);
      }
      if (
        showHelpMenu &&
        helpMenuRef.current &&
        !helpMenuRef.current.contains(event.target as Node) &&
        helpButtonRef.current &&
        !helpButtonRef.current.contains(event.target as Node)
      ) {
        setShowHelpMenu(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu, showHelpMenu]);

  return (
    <header className="bg-super-dark-purple text-white px-6 py-4">
      <div className="flex items-center justify-between">
        {/* Left side - Logo and Project Selector */}
        <div className="flex items-center space-x-6 flex-1">
          <div className="flex items-center space-x-2">
            <img
              src="/images/jarvis_logo.png"
              alt="Jarvis Logo"
              className="h-14 w-40 object-contain"
              style={{ display: 'block' }}
            />
            <span className="text-xl font-bold text-super-dark-purple">JARVIS</span>
          </div>

          {showProjectSelector && (
            <div className="flex items-center space-x-2" data-tour="project-selector">
              <span className="text-sm text-gray-300">CURRENT PROJECT</span>
              <ProjectSelector />
            </div>
          )}

        </div>

        {/* Right side - Help, Notifications and User menu */}
        <div className="flex items-center space-x-3">

          {/* Help Dropdown */}
          <div className="relative">
            <button
              ref={helpButtonRef}
              data-tour="help-button"
              onClick={() => setShowHelpMenu(p => !p)}
              className="flex items-center space-x-2 hover:bg-dusky-purple px-3 py-2 rounded-lg transition-colors"
              title="Help"
            >
              <HelpCircle className="w-5 h-5" />
              <span className="text-sm hidden md:inline">Help</span>
            </button>

            {showHelpMenu && (
              <div ref={helpMenuRef} className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50">
                <button
                  onClick={() => { navigate('/app/faq'); setShowHelpMenu(false); }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-3 transition-colors"
                >
                  <MessageCircle className="w-4 h-4 text-gray-500" />
                  <span>FAQs</span>
                </button>
                <button
                  onClick={() => { navigate('/app/faq-management'); setShowHelpMenu(false); }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-3 transition-colors"
                >
                  <BookOpen className="w-4 h-4 text-gray-500" />
                  <span>FAQ Management</span>
                </button>
                <div className="border-t border-gray-100 mt-1 pt-1">
                  <button
                    onClick={() => { showOnboardingForReturningUser(); setShowHelpMenu(false); }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-3 transition-colors"
                  >
                    <HelpCircle className="w-4 h-4 text-gray-500" />
                    <span>View Tutorial</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Notification Center */}
          <NotificationCenter />

          {/* User Menu */}
          <div className="relative">
            <button
              ref={userButtonRef}
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center space-x-2 hover:bg-dusky-purple px-3 py-2 rounded-lg transition-colors"
            >
              <div className="w-8 h-8 bg-electric-blue rounded-full flex items-center justify-center text-super-dark-purple font-semibold">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <ChevronDown className="w-4 h-4" />
            </button>

            {showUserMenu && (
              <div ref={userMenuRef} className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50">
                <div className="px-4 py-3 border-b border-gray-100">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-lg font-bold text-white">
                        {user?.name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{user?.name}</p>
                      <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                      <div className="flex items-center mt-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          user?.role === 'SUPER_ADMIN' ? 'bg-red-100 text-red-700' :
                          user?.role === 'MASTER' ? 'bg-purple-100 text-purple-700' :
                          user?.role === 'ADMIN' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          <Shield className="w-3 h-3 mr-1" />
                          {user?.role}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="py-1">
                  <button
                    onClick={() => {
                      navigate('/app/settings');
                      setShowUserMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-3 transition-colors"
                  >
                    <Settings className="w-4 h-4 text-gray-500" />
                    <span>Settings</span>
                  </button>
                  {/* Integrations lives under Admin → Identity & Setup only
                      (firm Slack/Zoho + org Outlook). Do not re-add it here. */}
                </div>
                
                <div className="border-t border-gray-100 pt-1">
                  <button
                    onClick={() => {
                      logout();
                      setShowUserMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-3 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
    </header>
  );
};

export default Header;